/**
 * MapMeasureTool – floating measure / draw toolbar for Leaflet maps.
 *
 * Improvements over v1:
 *  1. Vincenty formula (WGS-84 ellipsoid) replaces Haversine for ~0.5mm accuracy
 *     vs Haversine's ~0.3–0.5% error on long distances.
 *  2. Snap-to-point: when the cursor comes within SNAP_PX pixels of an existing
 *     measurement point, the click snaps to that exact coordinate, eliminating
 *     drift when reusing anchor points.
 *  3. Snap indicator: a pulsing crosshair ring renders at the snap target so the
 *     user has clear visual feedback before clicking.
 *
 * Props:
 *   map         – Leaflet map instance (L.Map) or null
 *   top         – CSS top offset for the toolbar (default 52)
 *   right       – CSS right offset (default 12)
 *   tableOffset – bottom offset for mode hint (default 240)
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import L from 'leaflet';
import { T } from '../assets/theme';

// ─── Constants ───────────────────────────────────────────────────────────────

const M_TO_NM   = 1 / 1852;
const SNAP_PX   = 12;   // pixel radius within which cursor snaps to existing point

// WGS-84 ellipsoid parameters
const WGS84 = {
    a:  6378137.0,          // semi-major axis (m)
    b:  6356752.314245,     // semi-minor axis (m)
    f:  1 / 298.257223563,  // flattening
};

// ─── Vincenty inverse formula ─────────────────────────────────────────────────
// Returns the geodesic distance in metres between two lat/lon points using the
// WGS-84 ellipsoid. Falls back to Haversine if the iterative solution fails to
// converge (e.g. nearly antipodal points).
function vincentyM(lat1, lon1, lat2, lon2) {
    const { a, b, f } = WGS84;
    const toRad = d => d * Math.PI / 180;

    const φ1 = toRad(lat1), φ2 = toRad(lat2);
    const L_  = toRad(lon2 - lon1);

    const U1 = Math.atan((1 - f) * Math.tan(φ1));
    const U2 = Math.atan((1 - f) * Math.tan(φ2));
    const sinU1 = Math.sin(U1), cosU1 = Math.cos(U1);
    const sinU2 = Math.sin(U2), cosU2 = Math.cos(U2);

    let λ = L_, λPrev, iter = 0;
    let sinσ, cosσ, σ, sinα, cos2α, cos2σm, C;

    do {
        const sinλ = Math.sin(λ), cosλ = Math.cos(λ);
        sinσ   = Math.sqrt((cosU2 * sinλ) ** 2 + (cosU1 * sinU2 - sinU1 * cosU2 * cosλ) ** 2);
        if (sinσ === 0) return 0; // coincident points

        cosσ   = sinU1 * sinU2 + cosU1 * cosU2 * cosλ;
        σ      = Math.atan2(sinσ, cosσ);
        sinα   = (cosU1 * cosU2 * sinλ) / sinσ;
        cos2α  = 1 - sinα ** 2;
        cos2σm = cos2α !== 0 ? cosσ - (2 * sinU1 * sinU2) / cos2α : 0;
        C      = (f / 16) * cos2α * (4 + f * (4 - 3 * cos2α));

        λPrev  = λ;
        λ = L_ + (1 - C) * f * sinα * (σ + C * sinσ * (cos2σm + C * cosσ * (-1 + 2 * cos2σm ** 2)));
    } while (Math.abs(λ - λPrev) > 1e-12 && ++iter < 200);

    if (iter >= 200) {
        // Fallback: Haversine (handles near-antipodal edge cases)
        const R = 6371000;
        const dLat = toRad(lat2 - lat1), dLon = toRad(lon2 - lon1);
        const aa   = Math.sin(dLat / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dLon / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(aa), Math.sqrt(1 - aa));
    }

    const uSq = cos2α * (a ** 2 - b ** 2) / b ** 2;
    const A_  = 1 + uSq / 16384 * (4096 + uSq * (-768 + uSq * (320 - 175 * uSq)));
    const B_  = uSq / 1024       * ( 256 + uSq * (-128 + uSq * ( 74 -  47 * uSq)));
    const Δσ  = B_ * sinσ * (cos2σm + B_ / 4 * (cosσ * (-1 + 2 * cos2σm ** 2)
              - B_ / 6 * cos2σm * (-3 + 4 * sinσ ** 2) * (-3 + 4 * cos2σm ** 2)));

    return b * A_ * (σ - Δσ);
}

// ─── Formatting ──────────────────────────────────────────────────────────────

function fmtDist(m) {
    const nm = (m * M_TO_NM).toFixed(3);
    if (m >= 1000) return `${(m / 1000).toFixed(3)} km  /  ${nm} NM`;
    return `${m.toFixed(1)} m  /  ${nm} NM`;
}

// ─── Leaflet helpers ──────────────────────────────────────────────────────────

function labelIcon(num, text, borderColor) {
    return L.divIcon({
        className: '',
        html: `<div style="background:rgba(6,14,26,0.92);color:#fff;padding:3px 9px;border-radius:6px;font-size:11px;font-weight:700;white-space:nowrap;border:1px solid ${borderColor};pointer-events:none;box-shadow:0 2px 8px rgba(0,0,0,0.5);"><span style="color:#3a8ac4;margin-right:6px;">${num}.</span>${text}</div>`,
        iconAnchor: [0, 0],
    });
}

// Snap indicator: a pulsing crosshair ring rendered as a Leaflet marker
function snapIcon() {
    return L.divIcon({
        className: '',
        html: `<div style="
            width:24px;height:24px;
            border:2px solid #F5A623;
            border-radius:50%;
            box-shadow:0 0 0 2px rgba(245,166,35,0.3);
            animation:snap-pulse 0.7s ease-in-out infinite alternate;
            pointer-events:none;
            position:relative;
        ">
            <style>
                @keyframes snap-pulse {
                    from { box-shadow: 0 0 0 2px rgba(245,166,35,0.3); }
                    to   { box-shadow: 0 0 0 6px rgba(245,166,35,0.0); }
                }
            </style>
            <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:4px;height:4px;background:#F5A623;border-radius:50%;"></div>
        </div>`,
        iconAnchor: [12, 12],
        iconSize:   [24, 24],
    });
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function MapMeasureTool({ map, top = 52, right = 12, tableOffset = 240 }) {
    const [mode, setMode]               = useState(null);
    const [results, setResults]         = useState([]);
    const [circlePanel, setCirclePanel] = useState(null);
    const [circleR, setCircleR]         = useState('5');
    const [circleUnit, setCircleUnit]   = useState('nm');

    const nextIdRef    = useRef(1);
    const layersRef    = useRef([]);
    const stateRef     = useRef({ mode: null, pts: [] });
    // All snappable coordinates: array of [lat, lng]
    const snapPtsRef   = useRef([]);
    // The live snap indicator marker (or null)
    const snapMarkerRef = useRef(null);

    useEffect(() => { stateRef.current.mode = mode; }, [mode]);

    // ── Cursor style ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (!map) return;
        const container = map.getContainer();
        const styleId   = 'measure-tool-cursor-style';
        let styleEl     = document.getElementById(styleId);

        if (mode) {
            if (!styleEl) {
                styleEl = document.createElement('style');
                styleEl.id = styleId;
                document.head.appendChild(styleEl);
            }
            styleEl.textContent = `.leaflet-container.measuring-active,.leaflet-container.measuring-active *{cursor:crosshair !important;}`;
            container.classList.add('measuring-active');
        } else {
            container.classList.remove('measuring-active');
            if (styleEl) styleEl.remove();
        }
        return () => {
            container.classList.remove('measuring-active');
            const el = document.getElementById(styleId);
            if (el) el.remove();
        };
    }, [mode, map]);

    // ── Layer helpers ─────────────────────────────────────────────────────────
    const removeLayer = useCallback((l) => {
        try { map?.removeLayer(l); } catch {}
    }, [map]);

    const clearAll = useCallback(() => {
        layersRef.current.forEach(removeLayer);
        layersRef.current = [];
        stateRef.current.pts = [];
        snapPtsRef.current   = [];
        if (snapMarkerRef.current) {
            try { map?.removeLayer(snapMarkerRef.current); } catch {}
            snapMarkerRef.current = null;
        }
        setResults([]);
        nextIdRef.current = 1;
        setCirclePanel(null);
    }, [removeLayer, map]);

    const activateMode = useCallback((m) => {
        stateRef.current.pts = [];
        setCirclePanel(null);
        setMode(prev => prev === m ? null : m);
    }, []);

    // Register a coordinate as a future snap target
    const registerSnapPt = useCallback(([lat, lng]) => {
        snapPtsRef.current.push([lat, lng]);
    }, []);

    // ── Snap helper: given a DOM event, return the best LatLng ────────────────
    // If within SNAP_PX of an existing point → return that point exactly.
    const resolveLatLng = useCallback((e) => {
        if (!map) return null;
        const container = map.getContainer();
        const rect      = container.getBoundingClientRect();
        const px        = L.point(e.clientX - rect.left, e.clientY - rect.top);
        const raw       = map.containerPointToLatLng(px);

        let best = null, bestDist = Infinity;
        for (const [slat, slng] of snapPtsRef.current) {
            const sPx = map.latLngToContainerPoint([slat, slng]);
            const d   = Math.hypot(px.x - sPx.x, px.y - sPx.y);
            if (d < SNAP_PX && d < bestDist) { bestDist = d; best = [slat, slng]; }
        }
        return best ? { lat: best[0], lng: best[1], snapped: true } : { lat: raw.lat, lng: raw.lng, snapped: false };
    }, [map]);

    // ── Mousemove → update snap indicator ────────────────────────────────────
    useEffect(() => {
        if (!map) return;
        const container = map.getContainer();

        const onMouseMove = (e) => {
            if (!stateRef.current.mode) {
                if (snapMarkerRef.current) {
                    try { map.removeLayer(snapMarkerRef.current); } catch {}
                    snapMarkerRef.current = null;
                }
                return;
            }
            const rect = container.getBoundingClientRect();
            const px   = L.point(e.clientX - rect.left, e.clientY - rect.top);

            let snapTarget = null;
            let bestDist   = Infinity;
            for (const [slat, slng] of snapPtsRef.current) {
                const sPx = map.latLngToContainerPoint([slat, slng]);
                const d   = Math.hypot(px.x - sPx.x, px.y - sPx.y);
                if (d < SNAP_PX && d < bestDist) { bestDist = d; snapTarget = [slat, slng]; }
            }

            if (snapTarget) {
                if (!snapMarkerRef.current) {
                    snapMarkerRef.current = L.marker(snapTarget, { icon: snapIcon(), zIndexOffset: 2000, interactive: false }).addTo(map);
                } else {
                    snapMarkerRef.current.setLatLng(snapTarget);
                }
            } else {
                if (snapMarkerRef.current) {
                    try { map.removeLayer(snapMarkerRef.current); } catch {}
                    snapMarkerRef.current = null;
                }
            }
        };

        container.addEventListener('mousemove', onMouseMove);
        return () => container.removeEventListener('mousemove', onMouseMove);
    }, [map]);

    // ── Map click handler ─────────────────────────────────────────────────────
    useEffect(() => {
        if (!map) return;
        const container = map.getContainer();

        const onDomClick = (e) => {
            const m = stateRef.current.mode;
            if (!m) return;
            e.stopPropagation();

            const { lat, lng } = resolveLatLng(e);

            // ── Circle mode ───────────────────────────────────────────────────
            if (m === 'circle') {
                setCirclePanel({ lat, lon: lng });
                registerSnapPt([lat, lng]);
                return;
            }

            // ── Distance mode ─────────────────────────────────────────────────
            if (m === 'distance') {
                const pts = stateRef.current.pts;
                const dot = L.circleMarker([lat, lng], {
                    radius: 5, color: T.blue, fillColor: T.blue, fillOpacity: 1, weight: 2,
                }).addTo(map);
                layersRef.current.push(dot);
                registerSnapPt([lat, lng]);

                if (pts.length === 0) {
                    stateRef.current.pts = [[lat, lng]];
                } else {
                    const [lat1, lon1] = pts[0];
                    const d       = vincentyM(lat1, lon1, lat, lng);
                    const distStr = fmtDist(d);
                    const num     = nextIdRef.current++;

                    const midLat  = (lat1 + lat) / 2;
                    const midLng  = (lon1 + lng) / 2;
                    const line    = L.polyline([[lat1, lon1], [lat, lng]], { color: T.blue, weight: 2, dashArray: '6 4' }).addTo(map);
                    const lbl     = L.marker([midLat, midLng], { icon: labelIcon(num, distStr, T.blue) }).addTo(map);
                    layersRef.current.push(line, lbl);
                    stateRef.current.pts = [];
                    setResults(prev => [...prev, { id: num, dist: distStr }]);
                }
                return;
            }

            // ── Line mode ─────────────────────────────────────────────────────
            if (m === 'line') {
                const pts = [...stateRef.current.pts, [lat, lng]];
                stateRef.current.pts = pts;
                const dot = L.circleMarker([lat, lng], {
                    radius: 4, color: '#F5A623', fillColor: '#F5A623', fillOpacity: 1, weight: 2,
                }).addTo(map);
                layersRef.current.push(dot);
                registerSnapPt([lat, lng]);

                if (pts.length >= 2) {
                    const [p1, p2] = [pts[pts.length - 2], pts[pts.length - 1]];
                    const seg = L.polyline([p1, p2], { color: '#F5A623', weight: 2 }).addTo(map);

                    // Label each line segment with its Vincenty distance
                    const d       = vincentyM(p1[0], p1[1], p2[0], p2[1]);
                    const distStr = fmtDist(d);
                    const num     = nextIdRef.current++;
                    const midLat  = (p1[0] + p2[0]) / 2;
                    const midLng  = (p1[1] + p2[1]) / 2;
                    const lbl     = L.marker([midLat, midLng], { icon: labelIcon(num, distStr, '#F5A623') }).addTo(map);
                    layersRef.current.push(seg, lbl);
                    setResults(prev => [...prev, { id: num, dist: distStr }]);
                }
            }
        };

        container.addEventListener('click', onDomClick, true);
        return () => container.removeEventListener('click', onDomClick, true);
    }, [map, resolveLatLng, registerSnapPt]);

    // ── Confirm circle ────────────────────────────────────────────────────────
    const confirmCircle = () => {
        if (!circlePanel || !map) return;
        let r = parseFloat(circleR) || 0;
        if (circleUnit === 'nm') r *= 1852;
        if (circleUnit === 'km') r *= 1000;
        if (r <= 0) return;

        const { lat, lon } = circlePanel;
        const circle = L.circle([lat, lon], { radius: r, color: T.blue, weight: 1.5, fillColor: T.blue, fillOpacity: 0.06 }).addTo(map);
        const ctr    = L.circleMarker([lat, lon], { radius: 4, color: T.blue, fillColor: T.blue, fillOpacity: 1, weight: 2 }).addTo(map);
        const lbl    = L.marker([lat, lon], { icon: labelIcon(nextIdRef.current++, `r = ${circleR} ${circleUnit.toUpperCase()}`, T.blue) }).addTo(map);
        layersRef.current.push(circle, ctr, lbl);
        setCirclePanel(null);
    };

    if (!map) return null;

    // ── Button helper ─────────────────────────────────────────────────────────
    const btn = (active, title, children, onClick) => (
        <button title={title} onClick={onClick} style={{
            padding: '6px 10px', border: 'none', cursor: 'pointer', borderRadius: 5, lineHeight: 1,
            background: active ? T.blue : 'transparent',
            color: active ? '#fff' : T.muted,
            fontSize: 15, transition: 'background 0.2s',
        }}>{children}</button>
    );

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <>
            {/* ── Toolbar ──────────────────────────────────────────────────── */}
            <div style={{
                position: 'absolute', top, right, zIndex: 1000,
                display: 'flex', gap: 2, background: 'rgba(6,14,26,0.88)',
                borderRadius: 8, padding: 4, border: `1px solid ${T.border}`,
                backdropFilter: 'blur(8px)',
            }}>
                {btn(mode === 'circle',   'Draw circle (click map to place center)',   '⊙', () => activateMode('circle'))}
                {btn(mode === 'distance', 'Measure distance (click two points)',       '↔', () => activateMode('distance'))}
                {btn(mode === 'line',     'Draw line (click points, re-click to end)', '✏', () => activateMode('line'))}
                <div style={{ width: 1, background: T.border, margin: '3px 2px' }} />
                {btn(false, 'Clear all measurements', '🗑', () => { clearAll(); setMode(null); })}
            </div>

            {/* ── Circle radius input ──────────────────────────────────────── */}
            {circlePanel && (
                <div style={{
                    position: 'absolute', top: top + 42, right, zIndex: 1001,
                    background: 'rgba(6,14,26,0.95)', border: `1px solid ${T.border}`,
                    borderRadius: 8, padding: '10px 12px', backdropFilter: 'blur(8px)',
                    display: 'flex', flexDirection: 'column', gap: 8, minWidth: 210,
                }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: T.white }}>Circle Radius</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                        <input
                            type="number" value={circleR} autoFocus
                            onChange={e => setCircleR(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && confirmCircle()}
                            style={{ flex: 1, padding: '5px 8px', borderRadius: 5, border: `1px solid ${T.border}`, background: 'rgba(255,255,255,0.07)', color: T.white, fontSize: 12, outline: 'none' }}
                        />
                        <select value={circleUnit} onChange={e => setCircleUnit(e.target.value)}
                            style={{ padding: '5px 6px', borderRadius: 5, border: `1px solid ${T.border}`, background: 'rgba(0,0,0,0.6)', color: T.white, fontSize: 12 }}>
                            <option value="m">m</option>
                            <option value="nm">NM</option>
                            <option value="km">km</option>
                        </select>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={confirmCircle} style={{ flex: 1, padding: '5px 0', borderRadius: 5, border: 'none', background: T.blue, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Draw</button>
                        <button onClick={() => setCirclePanel(null)} style={{ padding: '5px 10px', borderRadius: 5, border: `1px solid ${T.border}`, background: 'transparent', color: T.muted, fontSize: 12, cursor: 'pointer' }}>✕</button>
                    </div>
                </div>
            )}

            {/* ── Distance / line results list ─────────────────────────────── */}
            {results.length > 0 && (
                <div style={{
                    position: 'absolute', top: top + 42, right, zIndex: 1001,
                    background: 'rgba(6,14,26,0.95)', border: `1px solid ${T.blue}44`,
                    borderRadius: 8, padding: '10px 12px', backdropFilter: 'blur(8px)',
                    display: 'flex', flexDirection: 'column', gap: 6, minWidth: 160,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: T.muted, letterSpacing: '0.05em', marginBottom: 2 }}>MEASUREMENTS</div>
                    {results.map(r => (
                        <div key={r.id} style={{ fontSize: 12, color: T.white, fontWeight: 600, display: 'flex', gap: 8, alignItems: 'center' }}>
                            <span style={{ color: T.blue, fontWeight: 800 }}>{r.id}.</span>
                            <span>{r.dist}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* ── Mode hint ────────────────────────────────────────────────── */}
            {mode && !circlePanel && (
                <div style={{
                    position: 'absolute', bottom: tableOffset + 10, left: '50%',
                    transform: 'translateX(-50%)', zIndex: 1001,
                    background: 'rgba(6,14,26,0.85)', border: `1px solid ${T.border}`,
                    borderRadius: 6, padding: '5px 14px', fontSize: 11, color: T.muted,
                    backdropFilter: 'blur(6px)', pointerEvents: 'none', whiteSpace: 'nowrap',
                }}>
                    {mode === 'circle'   && 'Click map to place circle center'}
                    {mode === 'distance' && 'Click start point, then click end point · Snap to existing points'}
                    {mode === 'line'     && 'Click to add points · Snap to existing points · Click ✏ again to stop'}
                </div>
            )}
        </>
    );
}
